import { Text } from '@ltvco/refresh-lib/theme';
import { useSnackbar } from '@ltvco/refresh-lib/v1';

import { openPatentDownloadViaProxy } from './api';
import {
  DocumentTableCell,
  DocumentTableHeaderCell,
  DocumentTableHeaderRow,
  DocumentTableRow,
  DocumentsTable,
  ViewPdfLink,
} from './components';
import { formatPatentDate } from './utils';

import type { PatentDocument } from './types';

interface PatentDocumentsListProps {
  documents: PatentDocument[];
}

export const PatentDocumentsList = ({
  documents,
}: PatentDocumentsListProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleDownload = (downloadUrl: string) => {
    void openPatentDownloadViaProxy(downloadUrl).catch((err) => {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Document download failed.',
        { variant: 'error' }
      );
    });
  };

  return (
    <DocumentsTable>
      <DocumentTableHeaderRow>
        <DocumentTableHeaderCell>Document Description</DocumentTableHeaderCell>
        <DocumentTableHeaderCell>Date</DocumentTableHeaderCell>
        <DocumentTableHeaderCell>Document ID</DocumentTableHeaderCell>
        <DocumentTableHeaderCell>Action</DocumentTableHeaderCell>
      </DocumentTableHeaderRow>

      {documents.map((document) => {
        const pdfOption = document.downloadOptionBag?.find(
          (opt) => opt.mimeTypeIdentifier === 'PDF'
        );

        return (
          <DocumentTableRow key={document.documentIdentifier}>
            <DocumentTableCell>
              <Text variant="body2" sx={{ fontWeight: 500 }}>
                {document.documentCodeDescriptionText ||
                  document.documentCode ||
                  'Patent Document'}
              </Text>
            </DocumentTableCell>

            <DocumentTableCell>
              <Text
                variant="caption"
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  fontWeight: 700,
                  color: 'text.secondary',
                  mb: 0.25,
                }}
                aria-hidden="true"
              >
                Date
              </Text>
              <Text variant="body2" color="text.secondary">
                {document.officialDate
                  ? formatPatentDate(document.officialDate)
                  : '—'}
              </Text>
            </DocumentTableCell>

            <DocumentTableCell>
              <Text
                variant="caption"
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  fontWeight: 700,
                  color: 'text.secondary',
                  mb: 0.25,
                }}
                aria-hidden="true"
              >
                Document ID
              </Text>
              <Text variant="body2" color="text.secondary">
                {document.documentIdentifier || '—'}
              </Text>
            </DocumentTableCell>

            <DocumentTableCell>
              {pdfOption && (
                <>
                  <Text
                    variant="caption"
                    sx={{
                      display: { xs: 'block', sm: 'none' },
                      fontWeight: 700,
                      color: 'text.secondary',
                      mb: 0.25,
                    }}
                    aria-hidden="true"
                  >
                    Action
                  </Text>
                  <ViewPdfLink
                    type="button"
                    onClick={() => handleDownload(pdfOption.downloadUrl)}
                  >
                    View PDF
                  </ViewPdfLink>
                </>
              )}
            </DocumentTableCell>
          </DocumentTableRow>
        );
      })}
    </DocumentsTable>
  );
};
